/** @jsxImportSource @emotion/react */
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Tab, RequireAuth } from './components';
import { TabsProvider } from './context/Tabs';
import { PublicRouteLayout, SidebarLayout } from './layouts';
import { Login, PageNotFound, PageNotAuthorized } from './pages';
import { PasswordReset } from './pages/Login/components/PasswordReset';
import { formatRoutePath } from './utils';
import { getDefaultTabs } from './utils/getDefaultTabs';

interface Props {
  tabs: Tab[];
  merchantName: string | undefined;
}

const queryClient = new QueryClient();

export const App = ({ tabs, merchantName }: Props) => {
  const [allTabs, setAllTabs] = useState<Tab[]>([]);

  const updatedTabs = useMemo(() => {
    const [homeTab, ...rest] = getDefaultTabs();
    return [homeTab, ...tabs, ...rest];
  }, [tabs]);

  useEffect(() => {
    setAllTabs(updatedTabs);
  }, [updatedTabs]);

  return (
    <QueryClientProvider client={queryClient}>
      <TabsProvider tabs={allTabs}>
        <Routes>
          <Route element={<PublicRouteLayout />}>
            {/* public routes */}
            <Route path="/" element={<Navigate to="/home" replace />} />
            <Route path="login" element={<Login />} />
            <Route path="login/password-reset" element={<PasswordReset />} />
            <Route path="unauthorized" element={<PageNotAuthorized />} />
          </Route>

          {/* protected routes */}
          {allTabs.map((t, idx) => (
            <Route
              key={`${t.name}-${idx}`}
              element={<SidebarLayout merchantName={merchantName} />}
            >
              {/* protected route for tabs. */}
              <Route
                element={
                  <RequireAuth requiredPermissions={t.requiredPermissions} />
                }
              >
                <Route
                  key={t.name}
                  path={`${formatRoutePath(t.name)}/*`}
                  element={
                    !t.subTabs ? (
                      t.component
                    ) : (
                      <Navigate to={formatRoutePath(t.subTabs[0].name)} />
                    )
                  }
                />
              </Route>
              {/* protected route for subtabs. */}
              {(t.subTabs ?? []).map((st, idx) => (
                <Route
                  key={`${st.name}-${idx}`}
                  element={
                    <RequireAuth requiredPermissions={st.requiredPermissions} />
                  }
                >
                  <Route
                    key={st.name}
                    path={`${formatRoutePath(t.name)}/${formatRoutePath(
                      st.name
                    )}/*`}
                    element={st.component}
                  />
                </Route>
              ))}
            </Route>
          ))}

          {/* catch all non existing routes */}
          <Route path="*" element={<PageNotFound />} />
        </Routes>
      </TabsProvider>
    </QueryClientProvider>
  );
};
